// Simple utility to record worker sessions locally
import cashRegisterService from '../services/cashRegisterService';
import DeviationService from '../services/deviationService';
import saleCalculationService from '../services/SaleCalculationService';
import { getStorageEngine } from '../app/di/container';
import { readStoredCurrentBusiness } from '../services/offlineAuthSession';
import { kvGet, kvSet, kvRemove } from '../infrastructure/storage/kvAccess';
import {
  businessDataStorageKey,
  normalizeBusinessStorageId
} from './sharedCore';
import {
  getCashRegisterRepository,
  getBusinessPayloadRepository
} from '../app/di/container';
import {
  publishWorkerSessionUpdated,
  publishEcartUpdated,
  publishDeviationNotification,
  publishLegacyWindowEvent
} from '../infrastructure/events/legacyEventBridge';
import {
  loadAllWorkerSessionsForBusiness,
  loadOpenWorkerSessionsFromKeys,
  loadSessionsForWorker,
  persistWorkerSessions,
  persistWorkerSessionsPersisted,
  legacyWorkerSessionKeysForWorker
} from './workerSessionsStorage';
import { enrichSessionWithVentes } from './sessionSalesSync';
import { computeWorkerSessionDifferences } from '../domain/operations/workerSessionDeviation';
import { getCashRegisterOutflowContextForBusiness } from './cashRegisterOutflowContext';
import { pruneStaleWorkerSessions, reconcileAbandonedWorkerSessions } from './workerSessionSanitizer';
import {
  setCashRegisterPaused,
  getCashRegisterPaused,
  clearCashRegisterPaused
} from './cashRegisterPause';
import { generateWorkerSessionId } from './workerSessionDisplay';
import { formatCurrencyWithName } from './constants';
import { readActiveCurrencyType } from './posCurrencyUtils';
import {
  normalizeSaleTotal,
  normalizePaymentMethodForSession,
  dedupeSalesById,
} from './saleSessionHelpers';

function parseJsonBusiness(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

function parseCurrentBusiness() {
  return readStoredCurrentBusiness();
}

export const sessionRecorder = {
  // Thread-Safe Locking System
  locks: new Map(),

  // Thread-Safe Locking
  async withLock(lockKey, operation) {
    if (this.locks.has(lockKey)) {
      await this.locks.get(lockKey);
    }

    const lockPromise = new Promise(resolve => {
      this.locks.set(lockKey, resolve);
    });

    try {
      return await operation();
    } finally {
      this.locks.delete(lockKey);
    }
  },

  /**
   * Caisse verrouill├®e seulement si un autre travailleur a une session ouverte.
   * @param {string} businessId
   * @param {string|number|null} [exceptWorkerId] ÔÇö le travailleur qui se connecte (pas un blocage sur soi-m├¬me)
   */
  isCashRegisterLocked: (businessId, exceptWorkerId = null) => {
    try {
      const activeSessions = sessionRecorder.getActiveSessions(businessId);
      if (!activeSessions.length) return false;
      if (exceptWorkerId == null || exceptWorkerId === '') {
        return activeSessions.length > 0;
      }
      const selfId = String(exceptWorkerId);
      return activeSessions.some(
        (session) => session && String(session.workerId || '') !== selfId
      );
    } catch (error) {
      console.error('Error checking cash register lock status:', error);
      return false;
    }
  },

  // Get all sessions for a business
  getAllSessions: (businessId) => {
    try {
      return loadAllWorkerSessionsForBusiness(businessId);
    } catch (error) {
      console.error('Error getting all sessions:', error);
      return [];
    }
  },

  /**
   * Aligne newCashRegisterData (activeSession) et worker_sessions_* après logout partiel.
   * Un seul pass prune + reconcile (pas d'appels en triple côté RoleContext).
   */
  reconcileCashRegisterLockState(businessId) {
    if (!businessId) return;

    try {
      pruneStaleWorkerSessions(businessId);
      // undefined → resolve active from NCR / role; never force-close known open shifts
      reconcileAbandonedWorkerSessions(businessId);
      getCashRegisterRepository().repairOrphanSessions(businessId);

      const ncrActive = getCashRegisterRepository().getActiveSession(businessId);
      if (!ncrActive || ncrActive.logoutTime) return;

      const workerSessions = sessionRecorder.getWorkerSessions(
        ncrActive.workerId,
        businessId
      );
      const hasOpenKv = workerSessions.some((s) => s && !s.logoutTime);
      if (hasOpenKv) return;

      // NCR still active but worker_sessions closed → drop lock without inventing a new count
      const finalAmount =
        Number(ncrActive.finalAmount) ||
        Number(ncrActive.initialAmount) ||
        0;
      const ended = getCashRegisterRepository().endSession(
        ncrActive.workerId,
        finalAmount,
        businessId
      );
      if (!ended?.success) {
        getCashRegisterRepository().forceClearActiveSession(businessId);
        getCashRegisterRepository().repairOrphanSessions(businessId);
      }
    } catch (error) {
      console.warn('[sessionRecorder] reconcileCashRegisterLockState:', error?.message || error);
    }
  },

  reconcileAbandonedWorkerSessions(businessId, activeWorkerId) {
    return reconcileAbandonedWorkerSessions(businessId, activeWorkerId);
  },

  // Get active sessions for a business (source de vérité : activeSession + worker_sessions)
  getActiveSessions: (businessId) => {
    try {
      if (!businessId) return [];

      sessionRecorder.reconcileCashRegisterLockState(businessId);

      const active = [];
      const seenWorkers = new Set();

      const ncrActive = getCashRegisterRepository().getActiveSession(businessId);
      if (ncrActive && !ncrActive.logoutTime) {
        active.push({
          workerId: ncrActive.workerId,
          workerName: ncrActive.workerName,
          loginTime: ncrActive.loginTime,
          logoutTime: ncrActive.logoutTime,
          initialAmount: ncrActive.initialAmount,
          _source: 'newCashRegisterActive'
        });
        seenWorkers.add(String(ncrActive.workerId));
      }

      loadOpenWorkerSessionsFromKeys(businessId).forEach((session) => {
        const wid = String(session.workerId || '');
        if (wid && !seenWorkers.has(wid)) {
          active.push(session);
          seenWorkers.add(wid);
        }
      });

      return active;
    } catch (error) {
      console.error('Error getting active sessions:', error);
      return [];
    }
  },

  /**
   * Données pour l'écart — calcul central unique (identique Session-Manager / Logout).
   */
  getEcartSessionPayload(workerId, businessId, finalAmount = null, finalElectronicAmount = null) {
    const bid = normalizeBusinessStorageId(businessId) || businessId;
    const sessions = sessionRecorder.getWorkerSessions(workerId, bid);
    const rawActive =
      [...sessions].reverse().find((s) => s && !s.logoutTime) ||
      sessions[sessions.length - 1] ||
      null;

    const active = rawActive ? enrichSessionWithVentes(rawActive, bid) : null;
    if (!active) {
      return {
        sessionId: null,
        workerId,
        workerName: null,
        sales: [],
        initialAmount: 0,
        initialElectronicAmount: 0,
        cashSales: 0,
        electronicSales: 0,
        expectedAmount: 0,
        expectedElectronicAmount: 0,
        actualAmount: Number(finalAmount) || 0,
        actualElectronicAmount: Number(finalElectronicAmount) || 0,
        difference: 0,
        electronicDifference: 0,
        totalDifference: 0
      };
    }

    const sales = dedupeSalesById(Array.isArray(active?.sales) ? active.sales : []);
    const sessionForCalc = {
      ...active,
      businessId: bid,
      ...(finalAmount != null
        ? { finalCashAmount: Number(finalAmount) || 0, cashAmount: Number(finalAmount) || 0 }
        : {}),
      ...(finalElectronicAmount != null
        ? { finalElectronicAmount: Number(finalElectronicAmount) || 0 }
        : {})
    };

    const diff = computeWorkerSessionDifferences(
      sessionForCalc,
      getCashRegisterOutflowContextForBusiness(bid)
    );
    const detail = diff.deviationDetail || {};

    return {
      sessionId: active?.loginTime || active?.id,
      workerId: active?.workerId || workerId,
      workerName: active?.workerName,
      sales,
      ...detail,
      finalAmount: diff.finalCashAmount,
      finalElectronicAmount: diff.finalElectronic,
      expectedCash: diff.expectedCash,
      expectedElectronic: diff.expectedElectronic
    };
  },

  // Record when a worker logs in - Thread-Safe
  async recordLogin(workerId, workerName, cashAmount, businessId, actionType = 'confirm', electronicAmount = 0, amountsByProvider = null) {
    const lockKey = `session_${workerId}_${businessId}`;
    
    return await this.withLock(lockKey, async () => {
      try {
        // If businessId is not provided, try to get it from localStorage
        let finalBusinessId = businessId;
        if (!finalBusinessId) {
          const business = parseCurrentBusiness();
          if (business) {
            finalBusinessId = business.id;
          }
        }
        
        if (!finalBusinessId) {
          console.error('No businessId available for session recording');
          return;
        }
      
        // Get current currency setting
        const currentCurrency = kvGet('appCurrency') || 'MRO';
        
        // Get predecessor information from cash register service
        let predecessorInfo = null;
        try {
          const cashData = cashRegisterService.getCashRegisterData(finalBusinessId);
          
          if (cashData && cashData.sessions && cashData.sessions.length > 0) {
            // Get the most recent completed session
            const recentSession = cashData.sessions
              .filter(s => s.logoutTime)
              .sort((a, b) => new Date(b.logoutTime) - new Date(a.logoutTime))[0];
            
            if (recentSession && recentSession.workerId !== workerId) {
              predecessorInfo = {
                workerId: recentSession.workerId,
                workerName: recentSession.workerName,
                finalCashAmount: recentSession.finalCashAmount || recentSession.cashAmount,
                logoutTime: recentSession.logoutTime
              };
            }
          }
        } catch (error) {
          console.error('Error getting predecessor info:', error);
        }

        const existingSessions = sessionRecorder.getWorkerSessions(workerId, finalBusinessId);
        const openSession = [...existingSessions].reverse().find((s) => s && !s.logoutTime);

        if (openSession) {
          openSession.initialAmount = cashAmount;
          openSession.cashAmount = cashAmount;
          openSession.initialElectronicAmount = Number(electronicAmount) || 0;
          openSession.electronicAmount = Number(electronicAmount) || 0;
          openSession.actionType = actionType;
          if (amountsByProvider && typeof amountsByProvider === 'object') {
            openSession.initialAmountsByProvider = { ...amountsByProvider };
          }
          if (predecessorInfo) openSession.predecessorInfo = predecessorInfo;
        } else {
          const session = {
            id: generateWorkerSessionId(),
            loginTime: new Date().toISOString(),
            logoutTime: null,
            initialAmount: cashAmount,
            cashAmount: cashAmount,
            initialElectronicAmount: Number(electronicAmount) || 0,
            electronicAmount: Number(electronicAmount) || 0,
            initialAmountsByProvider:
              amountsByProvider && typeof amountsByProvider === 'object'
                ? { ...amountsByProvider }
                : undefined,
            finalCashAmount: null,
            workerId: workerId,
            workerName: workerName,
            actionType: actionType,
            currency: currentCurrency,
            predecessorInfo: predecessorInfo,
            sales: [],
            totalSales: 0,
            totalProfit: 0,
            saleCount: 0
          };
          existingSessions.push(session);
        }

        persistWorkerSessions(finalBusinessId, workerId, existingSessions);

        try {
          const activeCr = getCashRegisterRepository().getActiveSession(finalBusinessId);
          if (!activeCr) {
            getCashRegisterRepository().createSession(
              workerId,
              workerName,
              cashAmount,
              finalBusinessId
            );
          }
        } catch (crErr) {
          console.warn('[sessionRecorder] newCashRegister sync on login:', crErr?.message || crErr);
        }

        publishWorkerSessionUpdated({ workerId, businessId: finalBusinessId, action: 'login' });
      } catch (error) {
        console.error('Error recording worker login session:', error);
        throw error;
      }
    });
  },

  // Record when a worker pauses (temporary lock — only that cashier may resume)
  recordPause(workerId, businessId) {
    try {
      return setCashRegisterPaused(workerId, businessId);
    } catch (error) {
      console.error('Error recording pause:', error);
      return false;
    }
  },

  // Check if cash register is paused (any businessId form)
  isCashRegisterPaused(businessId) {
    try {
      return getCashRegisterPaused(businessId);
    } catch (error) {
      console.error('Error checking pause status:', error);
      return null;
    }
  },

  // Resume cash register (remove pause flags all key forms)
  resumeCashRegister(businessId) {
    try {
      return clearCashRegisterPaused(businessId);
    } catch (error) {
      console.error('Error resuming cash register:', error);
      return false;
    }
  },

  // Record when a worker logs out.
  // Returns { success, reason? } so callers can fail the UI instead of faking a clôture.
  async recordLogout(workerId, cashAmount, businessId, actionType = 'confirm', explanation = '', createDeviation = true, paymentBreakdown = null) {
    try {
      let finalBusinessId = businessId;
      if (!finalBusinessId) {
        const business = parseCurrentBusiness();
        if (business) {
          finalBusinessId = business.id;
        }
      }

      if (!finalBusinessId) {
        console.error('No businessId available for session recording');
        return { success: false, reason: 'missing_business_id' };
      }

      const existingSessions = sessionRecorder.getWorkerSessions(workerId, finalBusinessId);
      const openSession = [...existingSessions].reverse().find((s) => s && !s.logoutTime);
      const lastSession = openSession || null;

      if (lastSession) {
        // Compute écart on the still-open session, then mark completed.
        if (createDeviation) {
          const ecartPayload = sessionRecorder.getEcartSessionPayload(
            workerId,
            finalBusinessId,
            cashAmount,
            Number(paymentBreakdown?.electronicAmount) || 0
          );
          const deviation = DeviationService.deviationFromSessionDetail(ecartPayload);

          if (deviation.hasDeviation && !deviation.withinTolerance) {
            const selfDeviation = DeviationService.createSelfDeviation(
              ecartPayload,
              deviation,
              explanation
            );
            if (selfDeviation) {
              await sessionRecorder.saveDeviationToBusinessData(finalBusinessId, selfDeviation, 'self');
            }
          }
        }

        lastSession.logoutTime = new Date().toISOString();
        lastSession.finalCashAmount = cashAmount;
        lastSession.cashAmount = cashAmount;
        lastSession.status = 'completed';
        if (paymentBreakdown && typeof paymentBreakdown === 'object') {
          const countedElectronic = Number(paymentBreakdown.electronicAmount) || 0;
          lastSession.finalElectronicAmount = countedElectronic;
          // Ne pas écraser electronicAmount (souvent = ouverture) — le compté va dans finalElectronicAmount
          lastSession.totalCountedAmount =
            Number(paymentBreakdown.totalAmount) ||
            (Number(cashAmount) || 0) + countedElectronic;
          if (paymentBreakdown.amountsByProvider && typeof paymentBreakdown.amountsByProvider === 'object') {
            lastSession.finalAmountsByProvider = { ...paymentBreakdown.amountsByProvider };
          }
        }
        lastSession.logoutActionType = actionType;

        if (lastSession.sales && lastSession.sales.length > 0) {
          lastSession.totalSales = lastSession.sales.reduce(
            (sum, sale) => sum + normalizeSaleTotal(sale),
            0
          );
          lastSession.totalProfit = lastSession.sales.reduce((sum, sale) => sum + (sale.profit || 0), 0);
          lastSession.saleCount = lastSession.sales.length;
        }

        await persistWorkerSessionsPersisted(finalBusinessId, workerId, existingSessions);
      }

      try {
        await cashRegisterService.closeWorkerSessionOnLogout(workerId, cashAmount, finalBusinessId);
      } catch (legacyErr) {
        console.warn('[sessionRecorder] legacy cash register close on logout:', legacyErr?.message || legacyErr);
      }

      try {
        const activeCr = getCashRegisterRepository().getActiveSession(finalBusinessId);
        if (activeCr && String(activeCr.workerId) === String(workerId)) {
          getCashRegisterRepository().endSession(workerId, cashAmount, finalBusinessId);
        } else {
          getCashRegisterRepository().repairOrphanSessions(finalBusinessId);
        }
      } catch (crErr) {
        console.warn('[sessionRecorder] newCashRegister sync on logout:', crErr?.message || crErr);
        try {
          getCashRegisterRepository().forceClearActiveSession(finalBusinessId);
          getCashRegisterRepository().repairOrphanSessions(finalBusinessId);
        } catch (_) {
          /* best-effort */
        }
      }

      sessionRecorder.resumeCashRegister(finalBusinessId);

      if (typeof window !== 'undefined') {
        publishWorkerSessionUpdated({ workerId, businessId: finalBusinessId, action: 'logout' });
      }

      // Closing NCR alone when KV already had no open row is still a successful unlock.
      return { success: true, hadOpenSession: Boolean(lastSession) };
    } catch (error) {
      console.error('Error recording worker logout session:', error);
      return { success: false, reason: error?.message || 'logout_failed' };
    }
  },

  // Record a sale during an active session
  recordSale(workerId, saleData, businessId) {
    if (!workerId) {
      console.warn('[sessionRecorder] recordSale skipped: missing workerId');
      return { success: false, reason: 'missing_worker_id' };
    }

    let finalBusinessId = businessId;
    if (!finalBusinessId) {
      const business = parseCurrentBusiness();
      if (business) finalBusinessId = business.id;
    }

    if (!finalBusinessId) {
      console.warn('[sessionRecorder] recordSale skipped: no businessId');
      return { success: false, reason: 'no_business_id' };
    }

    const existingSessions = sessionRecorder.getWorkerSessions(workerId, finalBusinessId);

    if (existingSessions.length === 0) {
      console.warn('[sessionRecorder] recordSale skipped: no session for worker', workerId);
      return { success: false, reason: 'no_session' };
    }

    const lastSession = existingSessions[existingSessions.length - 1];

    if (!lastSession || lastSession.logoutTime) {
      console.warn('[sessionRecorder] recordSale skipped: no active session for', workerId);
      return { success: false, reason: 'no_active_session' };
    }

    try {
      
      // Calculate profit for this sale
      const profit = sessionRecorder.calculateSaleProfit(saleData, finalBusinessId);
      
      // Get current currency setting
      const currentCurrency = kvGet('appCurrency') || 'MRO';
      
      const saleTotal = normalizeSaleTotal(saleData);
      const actualPaymentMethod = normalizePaymentMethodForSession(saleData);

      const saleRecord = {
        id: saleData.id || saleData.orderId,
        orderId: saleData.orderId,
        date: saleData.date || new Date().toISOString(),
        total: saleTotal,
        totalAmount: saleTotal,
        profit: profit,
        currency: currentCurrency, // Store the currency used when sale was recorded
        products: saleData.products || [],
        customerName: saleData.customerName || '',
        // Payment method information - use the specific electronic method if available
        paymentMethod: actualPaymentMethod, // Store the actual payment method used (bankily, seddad, etc.)
        cashAmount: saleData.cashAmount || null, // Amount given by customer (for cash payments)
        change: saleData.change || null, // Change given back (for cash payments)
        electronicPaymentMethod: saleData.electronicPaymentMethod || null // Keep original electronic method for reference
      };

      // Debug logging to help troubleshoot payment method issues
      
      if (!Array.isArray(lastSession.sales)) {
        lastSession.sales = [];
      }

      const saleKey = String(saleRecord.id || saleRecord.orderId || '');
      const alreadyRecorded =
        saleKey &&
        lastSession.sales.some(
          (s) =>
            s &&
            (String(s.id) === saleKey || String(s.orderId) === saleKey)
        );
      if (alreadyRecorded) {
        return { success: true, session: lastSession, skipped: true, reason: 'duplicate' };
      }

      lastSession.sales.push(saleRecord);
      lastSession.sales = dedupeSalesById(lastSession.sales);

      // Update session totals
      lastSession.totalSales = lastSession.sales.reduce(
        (sum, sale) => sum + normalizeSaleTotal(sale),
        0
      );
      lastSession.totalProfit = lastSession.sales.reduce((sum, sale) => sum + (sale.profit || 0), 0);
      lastSession.saleCount = lastSession.sales.length;

      persistWorkerSessions(finalBusinessId, workerId, existingSessions);

      if (typeof window !== 'undefined') {
        publishWorkerSessionUpdated({
          workerId,
          businessId: finalBusinessId,
          action: 'sale',
          saleTotal
        });
      }

      return { success: true, session: lastSession };
    } catch (error) {
      console.error('Error recording sale in session:', error);
      return { success: false, reason: error?.message || 'record_failed' };
    }
  },

  // Ô£à FIX: Calculate profit for a sale based on lotsystem (FIFO)
  calculateSaleProfit(saleData, businessId) {
    try {
      if (!saleData.products || !Array.isArray(saleData.products)) {
        return 0;
      }
      
      // Ô£à Verwende SaleCalculationService f├╝r konsistente Berechnung
      
      // Hole Lots f├╝r dieses Business
      let lots = [];
      try {
        const lotsData = kvGet(`lots_${businessId}`);
        if (lotsData != null) {
          const parsed =
            typeof lotsData === 'string' ? JSON.parse(lotsData) : lotsData;
          lots = Array.isArray(parsed) ? parsed : Object.values(parsed || {});
        }
      } catch (error) {
        console.warn('Could not load lots for profit calculation:', error);
      }
      
      // ADR-0013: Sale-Profit über Finance-Owner (FIFO aus Sale-Zeilen als Mini-Sales)
      const { calculateProfit } = require('../domain/finance/profit');
      const syntheticSale = {
        products: saleData.products || [],
        total: (saleData.products || []).reduce(
          (sum, p) =>
            sum +
            (Number(p.quantity) || 0) *
              (Number(p.sellingUnitPrice || p.price) || 0),
          0
        )
      };
      syntheticSale.totalAmount = syntheticSale.total;
      const profit = calculateProfit({
        sales: [syntheticSale],
        purchases: [],
        expenses: [],
        lots
      });
      return profit.netProfit || 0;
    } catch (error) {
      console.error('Error calculating sale profit:', error);
      return 0;
    }
  },

  // Get all sessions for a specific worker
  getWorkerSessions(workerId, businessId) {
    try {
      const bid = normalizeBusinessStorageId(businessId) || businessId;
      return loadSessionsForWorker(bid, workerId);
    } catch (error) {
      console.error('Error getting worker sessions:', error);
      return [];
    }
  },

  // Get all sessions for all workers in a business
  getAllBusinessSessions(businessId) {
    try {
      return loadAllWorkerSessionsForBusiness(businessId);
    } catch (error) {
      console.error('Error getting all business sessions:', error);
      return [];
    }
  },

  /** Ferme les sessions ouvertes sans effacer l'historique (employ├® d├®sactiv├®). */
  closeActiveSessionsForWorker(workerId, businessId) {
    try {
      const bid = normalizeBusinessStorageId(businessId) || businessId;
      const nowIso = new Date().toISOString();
      const sessions = loadSessionsForWorker(bid, workerId);
      let changed = false;
      sessions.forEach((session) => {
        if (!session || session.logoutTime) return;
        session.logoutTime = nowIso;
        session.status = 'completed';
        session.finalCashAmount =
          session.finalCashAmount ?? session.initialAmount ?? session.cashAmount ?? 0;
        changed = true;
      });
      if (changed) {
        persistWorkerSessions(bid, workerId, sessions);
      }

      try {
        const active = getCashRegisterRepository().getActiveSession(bid);
        if (active && String(active.workerId) === String(workerId)) {
          getCashRegisterRepository().forceClearActiveSession(bid);
          getCashRegisterRepository().repairOrphanSessions(bid);
        }
      } catch (crErr) {
        console.warn('[sessionRecorder] closeActiveSessionsForWorker NCR:', crErr?.message || crErr);
      }

      const pause = getCashRegisterPaused(bid);
      if (pause && String(pause.workerId) === String(workerId)) {
        clearCashRegisterPaused(bid);
      }

      if (typeof window !== 'undefined') {
        publishWorkerSessionUpdated({ workerId, businessId: bid, action: 'force_close' });
      }
    } catch (error) {
      console.error('Error closing active worker sessions:', error);
    }
  },

  // Clear all sessions for a specific worker
  clearWorkerSessions(workerId, businessId) {
    try {
      const bid = normalizeBusinessStorageId(businessId) || businessId;
      const nowIso = new Date().toISOString();
      const sessions = loadSessionsForWorker(bid, workerId);
      let changed = false;
      sessions.forEach((session) => {
        if (!session || session.logoutTime) return;
        session.logoutTime = nowIso;
        session.status = 'completed';
        changed = true;
      });
      if (changed) {
        persistWorkerSessions(bid, workerId, sessions);
      }

      legacyWorkerSessionKeysForWorker(bid, workerId).forEach((key) => {
        kvRemove(key);
      });

      try {
        const active = getCashRegisterRepository().getActiveSession(bid);
        if (active && String(active.workerId) === String(workerId)) {
          getCashRegisterRepository().forceClearActiveSession(bid);
          getCashRegisterRepository().repairOrphanSessions(bid);
        }
      } catch (crErr) {
        console.warn('[sessionRecorder] clearWorkerSessions NCR:', crErr?.message || crErr);
      }

      const pause = getCashRegisterPaused(bid);
      if (pause && String(pause.workerId) === String(workerId)) {
        clearCashRegisterPaused(bid);
      }
    } catch (error) {
      console.error('Error clearing worker sessions:', error);
    }
  },

  // Clear all sessions for a business
  clearAllBusinessSessions(businessId) {
    try {
      const keys = getStorageEngine().listKvKeys();
      keys.forEach((key) => {
        if (key && key.startsWith(`worker_sessions_${businessId}_`)) {
          kvRemove(key);
        }
      });
    } catch (error) {
      console.error('Error clearing all business sessions:', error);
    }
  },

  // Get worker performance statistics
  getWorkerPerformanceStats(businessId, period = 'all') {
    try {
      const allSessions = sessionRecorder.getAllBusinessSessions(businessId);
      const now = new Date();
      let filteredSessions = allSessions;

      // Filter by period
      if (period !== 'all') {
        const cutoffDate = new Date();
        switch (period) {
          case 'today':
            cutoffDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            cutoffDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            cutoffDate.setMonth(now.getMonth() - 1);
            break;
        }
        filteredSessions = allSessions.filter(session => 
          new Date(session.loginTime) >= cutoffDate
        );
      }

      // Group by worker
      const workerStats = {};
      filteredSessions.forEach(session => {
        const workerId = session.workerId;
        if (!workerStats[workerId]) {
          workerStats[workerId] = {
            workerId,
            workerName: session.workerName,
            totalSessions: 0,
            totalSales: 0,
            totalRevenue: 0,
            totalProfit: 0,
            totalSessionTime: 0,
            averageSessionTime: 0,
            bestSession: null,
            worstSession: null
          };
        }

        const stats = workerStats[workerId];
        stats.totalSessions += 1;
        stats.totalSales += session.saleCount || 0;
        stats.totalRevenue += session.totalSales || 0;
        stats.totalProfit += session.totalProfit || 0;

        // Calculate session time
        if (session.loginTime && session.logoutTime) {
          const sessionTime = new Date(session.logoutTime) - new Date(session.loginTime);
          stats.totalSessionTime += sessionTime;
        }

        // Track best and worst sessions
        if (!stats.bestSession || (session.totalSales || 0) > (stats.bestSession.totalSales || 0)) {
          stats.bestSession = session;
        }
        if (!stats.worstSession || (session.totalSales || 0) < (stats.worstSession.totalSales || 0)) {
          stats.worstSession = session;
        }
      });

      // Calculate averages
      Object.values(workerStats).forEach(worker => {
        worker.averageSessionTime = worker.totalSessions > 0 ? 
          worker.totalSessionTime / worker.totalSessions : 0;
      });

      return Object.values(workerStats)
        .sort((a, b) => b.totalRevenue - a.totalRevenue);
    } catch (error) {
      console.error('Error getting worker performance stats:', error);
      return [];
    }
  },

  // Get sales trends
  getSalesTrends(businessId, period = 'week') {
    try {
      const allSessions = sessionRecorder.getAllBusinessSessions(businessId);
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (period) {
        case 'today':
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
      }

      const filteredSessions = allSessions.filter(session => 
        new Date(session.loginTime) >= cutoffDate
      );

      // Group by date
      const dailyStats = {};
      filteredSessions.forEach(session => {
        const date = new Date(session.loginTime).toISOString().split('T')[0];
        if (!dailyStats[date]) {
          dailyStats[date] = {
            date,
            totalSessions: 0,
            totalSales: 0,
            totalRevenue: 0,
            totalProfit: 0,
            workers: new Set()
          };
        }

        const dayStats = dailyStats[date];
        dayStats.totalSessions += 1;
        dayStats.totalSales += session.saleCount || 0;
        dayStats.totalRevenue += session.totalSales || 0;
        dayStats.totalProfit += session.totalProfit || 0;
        dayStats.workers.add(session.workerId);
      });

      // Convert Set to count and sort by date
      return Object.values(dailyStats)
        .map(day => ({
          ...day,
          uniqueWorkers: day.workers.size,
          workers: undefined // Remove the Set
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    } catch (error) {
      console.error('Error getting sales trends:', error);
      return [];
    }
  },

  // Save deviation to main business data (entity store direct + payload version)
  async saveDeviationToBusinessData(businessId, deviation, type) {
    if (!businessId || !deviation) return;

    const {
      readDiscrepancyManagementFromBestSource,
      writeDiscrepancyManagementDocument,
      refreshDiscrepancyManagementReadCache,
      clearDiscrepancyManagementReadCache
    } = await import('./discrepancyManagementReadSource');
    const { normalizeDiscrepancyManagement } = await import('./resolveDiscrepancyForEcarts');
    const { dispatchSyncBusinessFieldsFromKv } = await import('./businessStateSync');

    const applyDeviation = (rawDoc) => {
      const dm = normalizeDiscrepancyManagement(rawDoc);
      const deviationId = deviation.id;
      const deviationAmountMro = Math.abs(DeviationService.signedDeviationAmount(deviation));

      // Never persist / notify zero-amount noise (cash-only `difference` used to report 0
      // while electronic totalDifference was the real écart — or pure rounding ghosts).
      if (deviationAmountMro <= 0.01) {
        return { dm, notification: null };
      }

      const alreadyStored =
        dm.selfDeviations.some((d) => d.id === deviationId) ||
        dm.handoverDeviations.some((d) => d.id === deviationId);
      if (alreadyStored) {
        // Do not re-notify when the same deviation is saved again
        return { dm, notification: null };
      }

      if (type === 'self') {
        dm.selfDeviations.push(deviation);
      } else if (type === 'handover') {
        dm.handoverDeviations.push(deviation);
      }

      if (deviation.status === 'pending') {
        const pendingReview = {
          deviationId: deviation.id,
          type: deviation.type,
          workerId: deviation.workerId || deviation.previousWorkerId,
          workerName: deviation.workerName || deviation.previousWorkerName,
          amount: deviationAmountMro,
          timestamp: deviation.timestamp
        };
        if (!dm.pendingReviews.some((r) => r.deviationId === deviationId)) {
          dm.pendingReviews.push(pendingReview);
        }
      }

      const deviationAmountLabel = formatCurrencyWithName(deviationAmountMro, 'fr-MR');
      const activeCurrency = readActiveCurrencyType();

      const notification = {
        id: `deviation_${deviation.id}_${Date.now()}`,
        type: 'deviation_created',
        deviationId: deviation.id,
        deviationType: deviation.type,
        workerId: deviation.workerId || deviation.previousWorkerId,
        workerName: deviation.workerName || deviation.previousWorkerName,
        amount: deviationAmountMro,
        currency: activeCurrency,
        timestamp: deviation.timestamp,
        read: false,
        message:
          deviation.type === 'self'
            ? `Nouvel écart d'auto-calcul de ${deviation.workerName || 'Employé'} (${deviationAmountLabel})`
            : `Nouvel écart de remise entre ${deviation.previousWorkerName} et ${deviation.nextWorkerName} (${deviationAmountLabel})`
      };
      dm.notifications.push(notification);

      const auditEntry = DeviationService.createAuditEntry('deviation_created', {
        deviationId: deviation.id,
        type: deviation.type,
        workerId: deviation.workerId || deviation.previousWorkerId,
        amount: deviationAmountMro
      });
      dm.auditTrail.push(auditEntry);

      return { dm, notification };
    };

    try {
      clearDiscrepancyManagementReadCache(businessId);
      const current = await readDiscrepancyManagementFromBestSource(businessId);
      const { dm: updatedDoc, notification } = applyDeviation(current);

      // Zero-amount / within-tolerance: do not write or notify
      if (!notification) {
        return;
      }

      await writeDiscrepancyManagementDocument(businessId, updatedDoc);

      await getBusinessPayloadRepository().mutatePayload(
        businessId,
        (parsedData) => {
          if (!parsedData || typeof parsedData !== 'object') {
            return { discrepancyManagement: updatedDoc };
          }
          parsedData.discrepancyManagement = updatedDoc;
          return parsedData;
        },
        { authoritativeFields: ['discrepancyManagement'] }
      );

      const doc = await refreshDiscrepancyManagementReadCache(businessId);
      dispatchSyncBusinessFieldsFromKv(businessId, ['discrepancyManagement']);

      publishDeviationNotification({
        title:
          deviation.type === 'self'
            ? "Nouvel écart d'auto-calcul"
            : 'Nouvel écart de remise',
        message: notification.message,
        type: 'warning',
        category: 'ECART',
        persistent: true,
        data: {
          deviationId: deviation.id,
          deviationType: deviation.type,
          workerId: deviation.workerId || deviation.previousWorkerId,
          workerName: deviation.workerName || deviation.previousWorkerName,
          amount: notification.amount,
          eventKey: `deviation_${deviation.id}`,
          businessId
        }
      });

      if (typeof window !== 'undefined' && doc) {
        const allEcarts = [...(doc.selfDeviations || []), ...(doc.handoverDeviations || [])];
        const openEcarts = allEcarts.filter(
          (ecart) =>
            (ecart.status === 'open' || ecart.status === 'pending') &&
            Math.abs(DeviationService.signedDeviationAmount(ecart)) > 0.01
        );
        publishEcartUpdated({
          businessId,
          totalEcarts: allEcarts.length,
          openEcarts: openEcarts.length,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error saving deviation to business data:', error);
      throw error;
    }
  },

  // Check for handover deviation when next worker logs in
  checkHandoverDeviation(workerId, workerName, foundAmount, businessId) {
    try {
      // Get the most recent completed session
      const cashData = cashRegisterService.getCashRegisterData(businessId);
      
      if (cashData && cashData.sessions && cashData.sessions.length > 0) {
        const recentSession = cashData.sessions
          .filter(s => s.logoutTime)
          .sort((a, b) => new Date(b.logoutTime) - new Date(a.logoutTime))[0];
        
        if (recentSession && recentSession.workerId !== workerId) {
          const previousAmount = recentSession.finalCashAmount || recentSession.cashAmount;
          const deviation = DeviationService.calculateHandoverDeviation(previousAmount, foundAmount);
          
          // Nur echte Deviationen erstellen (nicht innerhalb der Toleranz)
          const tolerance = 0.01;
          if (Math.abs(deviation.difference) > tolerance) {
            const handoverDeviation = DeviationService.createHandoverDeviation(
              recentSession.workerId,
              recentSession.workerName,
              workerId,
              workerName,
              deviation,
              '' // Explanation will be provided by the worker
            );

            if (!handoverDeviation) {
              return { hasDeviation: false };
            }

            // Save deviation to main business data
            sessionRecorder.saveDeviationToBusinessData(businessId, handoverDeviation, 'handover');
            
            return {
              hasDeviation: true,
              deviation: handoverDeviation,
              previousWorker: {
                id: recentSession.workerId,
                name: recentSession.workerName
              }
            };
          }
        }
      }
      
      return { hasDeviation: false };
    } catch (error) {
      console.error('Error checking handover deviation:', error);
      return { hasDeviation: false };
    }
  },

  // Update deviation with explanation
  updateDeviationExplanation(deviationId, explanation, businessId) {
    if (!businessId || !deviationId) return;

    getBusinessPayloadRepository()
      .mutatePayload(businessId, (parsedData) => {
        if (!parsedData?.discrepancyManagement) {
          return parsedData;
        }

        const selfDeviation = parsedData.discrepancyManagement.selfDeviations.find(
          (d) => d.id === deviationId
        );
        if (selfDeviation) {
          selfDeviation.explanation = explanation;
        }

        const handoverDeviation = parsedData.discrepancyManagement.handoverDeviations.find(
          (d) => d.id === deviationId
        );
        if (handoverDeviation) {
          handoverDeviation.explanation = explanation;
        }

        const auditEntry = DeviationService.createAuditEntry('explanation_added', {
          deviationId,
          explanation
        });
        parsedData.discrepancyManagement.auditTrail.push(auditEntry);

        return parsedData;
      }, { authoritativeFields: ['discrepancyManagement'] })
      .then(async () => {
        const { refreshDiscrepancyManagementReadCache } = await import(
          './discrepancyManagementReadSource'
        );
        const { dispatchSyncBusinessFieldsFromKv } = await import('./businessStateSync');
        await refreshDiscrepancyManagementReadCache(businessId);
        dispatchSyncBusinessFieldsFromKv(businessId, ['discrepancyManagement']);
      })
      .catch((error) => {
        console.error('Error updating deviation explanation:', error);
      });
  }
};

export default sessionRecorder;
